import React, { useState, useEffect } from 'react';
import { getBackendUrl } from '@/config';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Shield,
  ShieldCheck,
  ShieldAlert,
  AlertTriangle,
  Activity,
  Users,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  RefreshCw,
  Download,
  Search,
  Filter,
  ChevronRight,
  Loader2,
  Calendar,
  TrendingUp,
  AlertCircle,
  Lock,
  Database,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = getBackendUrl();

// Severity color mapping
const getSeverityColor = (severity) => {
  switch (severity?.toLowerCase()) {
    case 'critical': return 'bg-red-500 text-white';
    case 'high': return 'bg-orange-500 text-white';
    case 'medium': return 'bg-yellow-500 text-black';
    case 'low': return 'bg-blue-500 text-white';
    default: return 'bg-gray-500 text-white';
  }
};

// Status badge component
const StatusBadge = ({ status }) => {
  const colors = {
    healthy: 'bg-green-100 text-green-800 border-green-200',
    warning: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    critical: 'bg-red-100 text-red-800 border-red-200',
    pending: 'bg-blue-100 text-blue-800 border-blue-200',
    completed: 'bg-green-100 text-green-800 border-green-200',
    overdue: 'bg-red-100 text-red-800 border-red-200',
  };
  
  return (
    <Badge variant="outline" className={colors[status] || colors.pending}>
      {status}
    </Badge>
  );
};

export default function ComplianceDashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Dashboard data
  const [dashboard, setDashboard] = useState(null);
  const [healthStatus, setHealthStatus] = useState(null);
  
  // Audit logs
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditFilters, setAuditFilters] = useState({ category: '', action: '' });
  const [auditCategories, setAuditCategories] = useState({ categories: [], actions: [] });
  
  // Security events
  const [securityEvents, setSecurityEvents] = useState([]);
  const [securitySummary, setSecuritySummary] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showResolveDialog, setShowResolveDialog] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState('');
  
  // Access reviews
  const [accessReviews, setAccessReviews] = useState([]);
  const [showCreateReviewDialog, setShowCreateReviewDialog] = useState(false);
  const [reviewType, setReviewType] = useState('quarterly');
  const [dueDays, setDueDays] = useState(30);
  
  // Retention policies
  const [retentionPolicies, setRetentionPolicies] = useState([]);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('access_token')}`
  });

  // Fetch dashboard data
  const fetchDashboard = async () => {
    try {
      const [dashboardRes, healthRes] = await Promise.all([
        axios.get(`${API_URL}/api/compliance/dashboard`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/compliance/health`, { headers: getAuthHeaders() })
      ]);
      setDashboard(dashboardRes.data);
      setHealthStatus(healthRes.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Failed to load compliance dashboard');
    }
  };

  // Fetch audit logs
  const fetchAuditLogs = async () => {
    try {
      const params = new URLSearchParams();
      if (auditFilters.category) params.append('category', auditFilters.category);
      if (auditFilters.action) params.append('action', auditFilters.action);
      params.append('limit', '100');
      
      const response = await axios.get(
        `${API_URL}/api/compliance/audit-logs?${params.toString()}`,
        { headers: getAuthHeaders() }
      );
      setAuditLogs(response.data);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    }
  };

  // Fetch audit categories
  const fetchAuditCategories = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/compliance/audit-logs/categories`,
        { headers: getAuthHeaders() }
      );
      setAuditCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Fetch security events
  const fetchSecurityEvents = async () => {
    try {
      const [eventsRes, summaryRes] = await Promise.all([
        axios.get(`${API_URL}/api/compliance/security/events?limit=50`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/compliance/security/summary?days=7`, { headers: getAuthHeaders() })
      ]);
      setSecurityEvents(eventsRes.data);
      setSecuritySummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching security events:', error);
    }
  };

  // Fetch access reviews
  const fetchAccessReviews = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/compliance/access-reviews?limit=100`,
        { headers: getAuthHeaders() }
      );
      setAccessReviews(response.data);
    } catch (error) {
      console.error('Error fetching access reviews:', error);
    }
  };

  // Fetch retention policies
  const fetchRetentionPolicies = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/compliance/retention-policies`,
        { headers: getAuthHeaders() }
      );
      setRetentionPolicies(response.data);
    } catch (error) {
      console.error('Error fetching retention policies:', error);
    }
  };

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchDashboard(),
        fetchAuditCategories(),
        fetchRetentionPolicies()
      ]);
      setLoading(false);
    };
    loadData();
  }, []);

  // Load tab-specific data when tab changes
  useEffect(() => {
    const loadTabData = async () => {
      if (activeTab === 'audit') await fetchAuditLogs();
      if (activeTab === 'security') await fetchSecurityEvents();
      if (activeTab === 'access') await fetchAccessReviews();
    };
    loadTabData();
  }, [activeTab, auditFilters]);

  // Acknowledge security event
  const handleAcknowledgeEvent = async (eventId) => {
    try {
      await axios.post(
        `${API_URL}/api/compliance/security/events/${eventId}/acknowledge`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('Event acknowledged');
      fetchSecurityEvents();
    } catch (error) {
      toast.error('Failed to acknowledge event');
    }
  };

  // Resolve security event
  const handleResolveEvent = async () => {
    if (!selectedEvent || resolutionNotes.length < 10) {
      toast.error('Please provide resolution notes (min 10 characters)');
      return;
    }
    
    try {
      await axios.post(
        `${API_URL}/api/compliance/security/events/${selectedEvent.id}/resolve`,
        { resolution_notes: resolutionNotes },
        { headers: getAuthHeaders() }
      );
      toast.success('Event resolved');
      setShowResolveDialog(false);
      setSelectedEvent(null);
      setResolutionNotes('');
      fetchSecurityEvents();
    } catch (error) {
      toast.error('Failed to resolve event');
    }
  };

  // Create bulk access reviews
  const handleCreateBulkReviews = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/api/compliance/access-reviews/create-bulk`,
        { review_type: reviewType, due_days: dueDays },
        { headers: getAuthHeaders() }
      );
      toast.success(`Created ${response.data.reviews_created} access reviews`);
      setShowCreateReviewDialog(false);
      fetchAccessReviews();
    } catch (error) {
      toast.error('Failed to create access reviews');
    }
  };

  // Verify audit integrity
  const handleVerifyIntegrity = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/compliance/audit-logs/verify-integrity`,
        { headers: getAuthHeaders() }
      );
      if (response.data.verified) {
        toast.success(`Integrity verified! ${response.data.entries_checked} entries checked`);
      } else {
        toast.error(`Integrity issues found: ${response.data.issues.length} problems`);
      }
    } catch (error) {
      toast.error('Failed to verify integrity');
    }
  };

  if (loading) {
    return (
      <DashboardLayout role={user?.role} title="SOC 2 Compliance" user={user}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role={user?.role} title="SOC 2 Compliance" user={user}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Shield className="h-6 w-6" />
              SOC 2 Compliance Dashboard
            </h1>
            <p className="text-muted-foreground">
              Monitor security, audit logs, and compliance status
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchDashboard}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Health Status Banner */}
        {healthStatus && (
          <Card className={`border-2 ${
            healthStatus.status === 'healthy' ? 'border-green-500 bg-green-50 dark:bg-green-950' :
            healthStatus.status === 'warning' ? 'border-yellow-500 bg-yellow-50 dark:bg-yellow-950' :
            'border-red-500 bg-red-50 dark:bg-red-950'
          }`}>
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {healthStatus.status === 'healthy' ? (
                    <ShieldCheck className="h-10 w-10 text-green-600" />
                  ) : healthStatus.status === 'warning' ? (
                    <ShieldAlert className="h-10 w-10 text-yellow-600" />
                  ) : (
                    <AlertTriangle className="h-10 w-10 text-red-600" />
                  )}
                  <div>
                    <h3 className="text-lg font-semibold">
                      Compliance Score: {healthStatus.compliance_score}%
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {healthStatus.unresolved_security_events} unresolved security events •{' '}
                      {healthStatus.overdue_access_reviews} overdue access reviews
                    </p>
                  </div>
                </div>
                <StatusBadge status={healthStatus.status} />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="overview" className="gap-2">
              <Activity className="h-4 w-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="audit" className="gap-2">
              <FileText className="h-4 w-4" />
              Audit Logs
            </TabsTrigger>
            <TabsTrigger value="security" className="gap-2">
              <ShieldAlert className="h-4 w-4" />
              Security Events
            </TabsTrigger>
            <TabsTrigger value="access" className="gap-2">
              <Users className="h-4 w-4" />
              Access Reviews
            </TabsTrigger>
            <TabsTrigger value="retention" className="gap-2">
              <Database className="h-4 w-4" />
              Data Retention
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {dashboard && (
              <>
                {/* Stats Grid */}
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">
                        Audit Logs (30d)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{dashboard.audit_logs?.total || 0}</div>
                      <p className="text-xs text-muted-foreground">
                        {dashboard.audit_logs?.by_category?.authentication || 0} auth events
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">
                        Security Events
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{dashboard.security_events?.total || 0}</div>
                      <p className="text-xs text-red-600">
                        {dashboard.security_events?.unresolved || 0} unresolved
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">
                        Access Reviews
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{dashboard.access_reviews?.total || 0}</div>
                      <p className="text-xs text-yellow-600">
                        {dashboard.access_reviews?.pending || 0} pending
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">
                        Retention Policies
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{dashboard.data_retention?.active_policies || 0}</div>
                      <p className="text-xs text-muted-foreground">
                        Active policies
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Recommendations */}
                {dashboard.recommendations?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <AlertCircle className="h-5 w-5 text-yellow-500" />
                        Recommendations
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {dashboard.recommendations.map((rec, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm">
                            <ChevronRight className="h-4 w-4 mt-0.5 text-muted-foreground" />
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

          {/* Audit Logs Tab */}
          <TabsContent value="audit" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Audit Logs</CardTitle>
                  <Button variant="outline" size="sm" onClick={handleVerifyIntegrity}>
                    <Lock className="h-4 w-4 mr-2" />
                    Verify Integrity
                  </Button>
                </div>
                <CardDescription>
                  Immutable record of all system activities
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Filters */}
                <div className="flex gap-4 mb-4">
                  <Select
                    value={auditFilters.category || "all"}
                    onValueChange={(val) => setAuditFilters(prev => ({ ...prev, category: val === "all" ? "" : val }))}
                  >
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {auditCategories.categories?.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Select
                    value={auditFilters.action || "all"}
                    onValueChange={(val) => setAuditFilters(prev => ({ ...prev, action: val === "all" ? "" : val }))}
                  >
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="All Actions" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Actions</SelectItem>
                      {auditCategories.actions?.map(act => (
                        <SelectItem key={act} value={act}>{act}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <ScrollArea className="h-[400px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Timestamp</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Action</TableHead>
                        <TableHead>User</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {auditLogs.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell className="text-xs">
                            {new Date(log.timestamp).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{log.category}</Badge>
                          </TableCell>
                          <TableCell>{log.action}</TableCell>
                          <TableCell className="text-sm">
                            {log.user_email || 'System'}
                          </TableCell>
                          <TableCell className="text-sm max-w-xs truncate">
                            {log.description}
                          </TableCell>
                          <TableCell>
                            {log.success ? (
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            ) : (
                              <XCircle className="h-4 w-4 text-red-500" />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security Events Tab */}
          <TabsContent value="security" className="space-y-4">
            {/* Summary Cards */}
            {securitySummary && (
              <div className="grid gap-4 md:grid-cols-4">
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Critical</span>
                      <Badge className="bg-red-500">{securitySummary.severity_counts?.critical || 0}</Badge>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">High</span>
                      <Badge className="bg-orange-500">{securitySummary.severity_counts?.high || 0}</Badge>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Medium</span>
                      <Badge className="bg-yellow-500 text-black">{securitySummary.severity_counts?.medium || 0}</Badge>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Low</span>
                      <Badge className="bg-blue-500">{securitySummary.severity_counts?.low || 0}</Badge>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            <Card>
              <CardHeader>
                <CardTitle>Security Events</CardTitle>
                <CardDescription>
                  Monitor and respond to security incidents
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Severity</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Title</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead>Time</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {securityEvents.map((event) => (
                        <TableRow key={event.id}>
                          <TableCell>
                            <Badge className={getSeverityColor(event.severity)}>
                              {event.severity}
                            </Badge>
                          </TableCell>
                          <TableCell>{event.event_type}</TableCell>
                          <TableCell className="max-w-xs truncate">{event.title}</TableCell>
                          <TableCell className="text-sm">
                            {event.source_user_email || event.source_ip || 'Unknown'}
                          </TableCell>
                          <TableCell className="text-xs">
                            {new Date(event.timestamp).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              {!event.acknowledged && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleAcknowledgeEvent(event.id)}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setSelectedEvent(event);
                                  setShowResolveDialog(true);
                                }}
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Access Reviews Tab */}
          <TabsContent value="access" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Access Reviews</CardTitle>
                    <CardDescription>
                      Periodic review of user access rights
                    </CardDescription>
                  </div>
                  <Button onClick={() => setShowCreateReviewDialog(true)}>
                    <Users className="h-4 w-4 mr-2" />
                    Create Reviews
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Review Period</TableHead>
                        <TableHead>Due Date</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {accessReviews.map((review) => (
                        <TableRow key={review.id}>
                          <TableCell>{review.user_email}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{review.user_role}</Badge>
                          </TableCell>
                          <TableCell>{review.review_period}</TableCell>
                          <TableCell className="text-sm">
                            {new Date(review.due_date).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            {review.is_overdue ? (
                              <Badge className="bg-red-500">Overdue</Badge>
                            ) : (
                              <StatusBadge status={review.status} />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Data Retention Tab */}
          <TabsContent value="retention" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Data Retention Policies</CardTitle>
                <CardDescription>
                  Manage how long different types of data are retained
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Policy Name</TableHead>
                      <TableHead>Data Type</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Retention Period</TableHead>
                      <TableHead>Action on Expiry</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {retentionPolicies.map((policy) => (
                      <TableRow key={policy.id}>
                        <TableCell className="font-medium">{policy.policy_name}</TableCell>
                        <TableCell>{policy.data_type}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{policy.data_category}</Badge>
                        </TableCell>
                        <TableCell>{policy.retention_period_days} days</TableCell>
                        <TableCell>
                          <Badge 
                            variant="outline"
                            className={
                              policy.action_on_expiry === 'delete' ? 'border-red-500 text-red-500' :
                              policy.action_on_expiry === 'archive' ? 'border-blue-500 text-blue-500' :
                              'border-yellow-500 text-yellow-500'
                            }
                          >
                            {policy.action_on_expiry}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                    {retentionPolicies.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                          No retention policies configured
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Resolve Security Event Dialog */}
      <Dialog open={showResolveDialog} onOpenChange={setShowResolveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve Security Event</DialogTitle>
            <DialogDescription>
              Provide resolution notes for this security event
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {selectedEvent && (
              <div className="p-4 bg-muted rounded-lg">
                <p className="font-medium">{selectedEvent.title}</p>
                <p className="text-sm text-muted-foreground">{selectedEvent.description}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Resolution Notes</Label>
              <Textarea
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                placeholder="Describe the resolution..."
                rows={4}
              />
              <p className="text-xs text-muted-foreground">Minimum 10 characters required</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResolveDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleResolveEvent} disabled={resolutionNotes.length < 10}>
              Resolve Event
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Access Reviews Dialog */}
      <Dialog open={showCreateReviewDialog} onOpenChange={setShowCreateReviewDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Access Reviews</DialogTitle>
            <DialogDescription>
              Create access reviews for all active users
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Review Type</Label>
              <Select value={reviewType} onValueChange={setReviewType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="quarterly">Quarterly Review</SelectItem>
                  <SelectItem value="annual">Annual Review</SelectItem>
                  <SelectItem value="ad-hoc">Ad-hoc Review</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Due in (days)</Label>
              <Input
                type="number"
                value={dueDays}
                onChange={(e) => setDueDays(parseInt(e.target.value) || 30)}
                min={1}
                max={90}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateReviewDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateBulkReviews}>
              Create Reviews
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
